<script>
import Icon from "../../components/Icon.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import Popup from "../../components/popup.vue";
import BaseLineChart from "../../components/charts/BaseLineChart.vue";
import {useReportStore} from "../../../stores/report";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import {useOrganizationStore} from "../../../stores/organizations";
import {CHART_COLORS} from "@/javascript/helpers/chartColors";
import Tooltip from "../../components/tooltip.vue";

export default {
  name: 'GivingTrendReport',
  components: {Tooltip, Popup, LoadingSpinner, Icon, BaseLineChart },

  props: {
    title: {
      type: String,
      default: 'Donor Trend Report'
    },
    description: {
      type: String,
      default: ''
    }
  },

  setup() {
    const reportStore = useReportStore();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();

    return { reportStore, analyticEventStore, organizationStore };
  },

  data() {
    return {
      // Period selection
      selectedPeriod: 'calendarYear',
      period1Start: null,
      period1End: null,
      period2Start: null,
      period2End: null,

      // Report state
      reportLoaded: false,
      loading: false,
      exporting: false,

      // Aggregate counts
      aggregateCounts: {
        new: 0,
        increased: 0,
        decreased: 0,
        lapsed: 0,
        reactivated: 0,
        unchanged: 0,
        inactive: 0
      },

      // Donor comparison chart
      donors: [],
      donorsLoading: false,
      selectedDonors: [],
      donorSearchQuery: '',
      chartData: null,
      chartLoading: false,
      chartUpdating: false,
      maxDonors: 5,
      selectedTrendFilter: null,

      // All donors data (for aggregate)
      allDonorsData: [],
      totalDonors: 0,

      popupMessage: "",
      paginatedDonorList: 50
    };
  },

  computed: {
    canRunReport() {
      return this.period1Start && this.period1End && this.period2Start && this.period2End && !this.loading;
    },

    donorTrendMap() {
      const map = new Map();
      this.allDonorsData.forEach(d => map.set(d.donor_id, d.trend));
      return map;
    },

    filteredDonors() {
      let results = this.donors;

      //Text Search
      if (this.donorSearchQuery.trim()) {
        const query = this.donorSearchQuery.toLowerCase().trim();
        results = results.filter(donor => {
          const name = `${donor.user_details?.f_name || ''} ${donor.user_details?.l_name || ''}`.toLowerCase();
          const email = (donor.user_details?.email || '').toLowerCase();
          return name.includes(query) || email.includes(query);
        });
      }

      //Trend Filter
      if(this.selectedTrendFilter){
        results = results.filter(donor => {
          return this.donorTrendMap.get(donor.id) === this.selectedTrendFilter;
        });
      }
      return results;
    },

    canAddDonor() {
      return this.selectedDonors.length < this.maxDonors;
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            align: 'end',
            display: true,
            labels: {
              usePointStyle: true,
              pointStyleWidth: 7,
              boxHeight: 5
            }
          },
          tooltip: {
            enabled: true,
            intersect: false,
            mode: 'nearest',
            callbacks: {
              label: (context) => {
                let label = context.dataset.label || '';
                let value = context.parsed.y;
                return `${label}: $${value.toLocaleString()}`;
              }
            }
          }
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            grid: { display: false },
            border: { display: false },
            beginAtZero: true,
            ticks: {
              callback: (value) => `$${value.toLocaleString()}`
            }
          }
        }
      };
    },
    visibleDonors() {
      return this.filteredDonors.slice(0, this.paginatedDonorList);
    },
    hasMoreDonors() {
      return this.filteredDonors.length > this.paginatedDonorList;
    },
  },
  watch: {
    donorSearchQuery() {
      this.paginatedDonorList = 50;
    },

    selectedTrendFilter() {
      this.paginatedDonorList = 50;
    }
  },
  mounted() {
    this.setDateRange('calendarYear');
    this.loadDonors();
  },

  methods: {
    loadDonors() {
      this.donorsLoading = true;
      this.reportStore.getUnscopedDonors()
        .then((donors) => {
          this.donors = donors;
        })
        .catch((e) => {
          console.error("Error loading donors", e);
          this.popupMessage = "Failed to load donor list";
          this.$refs.popup.show();
        })
        .finally(() => {
          this.donorsLoading = false;
        });
    },

    setDateRange(period) {
      this.selectedPeriod = period;
      if (period === 'custom') return;

      const today = new Date();
      const fyDate = this.organizationStore.organization?.fiscal_year_start_date;
      const fy = fyDate ? new Date(fyDate + 'T00:00:00') : new Date(today.getFullYear(), 0, 1);
      const fmt = (d) => d.toISOString().split('T')[0];

      switch (period) {
        case 'fiscalYear': {
          let currentFyStart = new Date(today.getFullYear(), fy.getMonth(), fy.getDate());
          if (currentFyStart > today) currentFyStart.setFullYear(currentFyStart.getFullYear() - 1);
          const currentFyEnd = new Date(currentFyStart.getFullYear() + 1, fy.getMonth(), fy.getDate() - 1);
          const prevFyStart = new Date(currentFyStart.getFullYear() - 1, fy.getMonth(), fy.getDate());
          const prevFyEnd = new Date(currentFyStart.getFullYear(), fy.getMonth(), fy.getDate() - 1);

          this.period1Start = fmt(prevFyStart);
          this.period1End = fmt(prevFyEnd);
          this.period2Start = fmt(currentFyStart);
          this.period2End = currentFyEnd > today ? fmt(today) : fmt(currentFyEnd);
          break;
        }
        case 'calendarYear': {
          this.period1Start = fmt(new Date(today.getFullYear() - 1, 0, 1));
          this.period1End = fmt(new Date(today.getFullYear() - 1, 11, 31));
          this.period2Start = fmt(new Date(today.getFullYear(), 0, 1));
          this.period2End = fmt(today);
          break;
        }
        case 'last12Months': {
          const end2 = new Date(today);
          const start2 = new Date(today);
          start2.setFullYear(start2.getFullYear() - 1);
          const end1 = new Date(start2);
          end1.setDate(end1.getDate() - 1);
          const start1 = new Date(end1);
          start1.setFullYear(start1.getFullYear() - 1);

          this.period1Start = fmt(start1);
          this.period1End = fmt(end1);
          this.period2Start = fmt(start2);
          this.period2End = fmt(end2);
          break;
        }
      }
    },

    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    aggregatePercent(count) {
      if (!this.totalDonors) return '0%';
      return Math.round((count / this.totalDonors) * 100) + '%';
    },

    runReport() {
      if (!this.canRunReport) return;
      this.loading = true;

      const orgId = this.organizationStore.organizationId;

      fetch(`/organizations/${orgId}/reports/donor_giving_trends?period_1_start=${this.period1Start}&period_1_end=${this.period1End}&period_2_start=${this.period2Start}&period_2_end=${this.period2End}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(res => res.json())
        .then(json => {
          this.aggregateCounts = json.aggregate;
          this.allDonorsData = json.donors;
          this.totalDonors = json.total_donors;
          this.reportLoaded = true;
          this.updateChart();
        })
        .catch(e => {
          console.error("Error loading report", e);
          this.popupMessage = "Failed to load report data";
          this.$refs.popup.show();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    exportExcel() {
      this.exporting = true;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'report-generated-giving_trends' });

      const donorIds = this.donors.map(d => d.id);
      const fmt = (d) => d;

      this.reportStore.createGivingTrendReport(
        donorIds, fmt(this.period1Start), fmt(this.period1End),
        fmt(this.period2Start), fmt(this.period2End)
      ).then((record) => {
        return this.reportStore.pollReport(record.id);
      }).then((record) => {
        if (record) {
          this.reportStore.downloadCSVReport(record);
        }
      }).catch((e) => {
        this.popupMessage = e?.message || "Something went wrong generating the report!";
        this.$refs.popup.show();
      }).finally(() => {
        this.exporting = false;
      });
    },

    selectDonor(donor) {
      if (this.selectedDonors.length >= this.maxDonors) return;
      if (this.selectedDonors.find(d => d.id === donor.id)) return;
      this.selectedDonors.push(donor);
      this.donorSearchQuery = '';
      this.updateChart();
    },

    removeDonor(donor) {
      this.selectedDonors = this.selectedDonors.filter(d => d.id !== donor.id);
      this.updateChart();
    },

    clearSelectedDonors() {
      this.selectedDonors = [];
      this.updateChart();
    },

    toggleChartDonor(donor) {
      if (this.donorIsSelected(donor)) {
        this.selectedDonors = this.selectedDonors.filter(d => d.id !== donor.id);
      } else {
        if (this.selectedDonors.length >= this.maxDonors) return;
        this.selectedDonors.push(donor);
      }
      this.updateChart();
    },

    donorIsSelected(donor) {
      return !!this.selectedDonors.find(d => d.id === donor.id);
    },

    updateChart() {
      if (this.selectedDonors.length === 0) {
        this.chartData = null;
        this.chartLoading = false;
        return;
      }

      this.chartLoading = !this.chartData;
      this.chartUpdating = true;
      const orgId = this.organizationStore.organizationId;
      const donorIds = this.selectedDonors.map(d => d.id);
      const params = new URLSearchParams();
      donorIds.forEach(id => params.append('donor_ids[]', id));
      params.set('start_date', this.period1Start);
      params.set('end_date', this.period2End);

      fetch(`/organizations/${orgId}/reports/donor_giving_timeline?${params.toString()}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(res => res.json())
        .then(json => {
          const lineColors = [
            CHART_COLORS.chart.blue,
            CHART_COLORS.chart.teal,
            CHART_COLORS.chart.purple,
            CHART_COLORS.chart.orange,
            CHART_COLORS.chart.green
          ];

          const timelineData = json.donors;
          if (!timelineData || timelineData.length === 0 || !timelineData[0]?.monthly) {
            this.chartData = null;
            return;
          }

          const labels = timelineData[0].monthly.map(m => m.label);
          const datasets = timelineData.map((donorData, index) => {
            const color = lineColors[index % lineColors.length];
            const trend = this.getDonorTrend({ id: donorData.donor_id });
            return {
              label: `${donorData.name}${trend ? ' (' + trend + ')' : ''}`,
              data: donorData.monthly.map(m => m.total / 100),
              borderColor: color,
              backgroundColor: color,
              pointBackgroundColor: color,
              borderWidth: 2,
              tension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
              fill: false
            };
          });

          this.chartData = { labels, datasets };
        })
        .catch(e => {
          console.error("Error loading chart data", e);
          this.chartData = null;
        })
        .finally(() => {
          this.chartLoading = false;
          this.chartUpdating = false;
        });
    },

    getDonorTrend(donor) {
      return this.donorTrendMap.get(donor.id) || '';
    },

    trendColor(trend) {
      const colors = {
        new: '#10BE5A',
        increased: '#2563EB',
        decreased: '#F97316',
        lapsed: '#D22B2B',
        reactivated: '#6D28D9',
        unchanged: '#CBD5E1'
      };
      return colors[trend] || '#CBD5E1';
    }
  }
};
</script>

<template>
  <div class="giving-trend-report">
    <!-- Header -->
    <div class="header">
      <div class="headline-3-regular">{{ title }}</div>
      <div v-if="description" class="body-2-light">{{ description }}</div>
    </div>

    <!-- Reporting Period Section -->
    <div class="report-section">
      <div class="section-title">Reporting Period</div>
      <div class="period-pills">
        <button
          v-for="pill in [
            { key: 'fiscalYear', label: 'Fiscal Year' },
            { key: 'calendarYear', label: 'Calendar Year' },
            { key: 'last12Months', label: 'Last 12 Months' },
            { key: 'custom', label: 'Custom Dates' }
          ]"
          :key="pill.key"
          type="button"
          class="period-pill"
          :class="{ active: selectedPeriod === pill.key }"
          @click="setDateRange(pill.key)"
        >
          {{ pill.label }}
        </button>
      </div>

      <!-- Date display / Custom inputs -->
      <div class="period-dates">
        <div class="period-date-group">
          <label class="period-date-label">Previous</label>
          <div v-if="selectedPeriod === 'custom'" class="flex gap-2 items-center">
            <input v-model="period1Start" type="date" class="date-input" />
            <span class="text-gray-400">–</span>
            <input v-model="period1End" type="date" class="date-input" />
          </div>
          <div v-else class="period-date-display">
            {{ formatDisplayDate(period1Start) }} – {{ formatDisplayDate(period1End) }}
          </div>
        </div>
        <div class="period-date-group">
          <label class="period-date-label">Current</label>
          <div v-if="selectedPeriod === 'custom'" class="flex gap-2 items-center">
            <input v-model="period2Start" type="date" class="date-input" />
            <span class="text-gray-400">–</span>
            <input v-model="period2End" type="date" class="date-input" />
          </div>
          <div v-else class="period-date-display">
            {{ formatDisplayDate(period2Start) }} – {{ formatDisplayDate(period2End) }}
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button
          class="btn btn-filled"
          :disabled="!canRunReport"
          @click="runReport"
        >
          Run Report
        </button>
        <button
          class="btn btn-outline btn-secondary"
          :disabled="!canRunReport || exporting"
          @click="exportExcel"
        >
          <icon icon="cloud-arrow-down" size="16px" color="currentColor" />
          <span>{{ exporting ? 'Exporting...' : 'Export Excel' }}</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12">
      <loading-spinner size="small" />
    </div>

    <!-- Aggregate Counts -->
    <div v-if="reportLoaded" class="report-section aggregate-section">
      <div class="aggregate-header">
        <span class="section-title">Donor Summary</span>
        <span class="aggregate-total">{{ totalDonors }} total donors</span>
      </div>
      <div class="aggregate-row">
        <div class="aggregate-item">
          <div class="aggregate-label-row">
            <span class="aggregate-label">New</span>
            <tooltip message="Gave in the current period but had no giving history before it." variant="question" size="11px" :position="{ bottom: '100%', right: '0', transform: 'translateX(-5%)', maxWidth: '200px' }" />
          </div>
          <span class="aggregate-count">{{ aggregateCounts.new }}</span>
          <span class="aggregate-percent">{{ aggregatePercent(aggregateCounts.new) }}</span>
        </div>
        <div class="aggregate-item">
          <div class="aggregate-label-row">
            <span class="aggregate-label">Increased</span>
            <tooltip message="Gave more in the current period than the previous period." variant="question" size="11px" />
          </div>
          <span class="aggregate-count">{{ aggregateCounts.increased }}</span>
          <span class="aggregate-percent">{{ aggregatePercent(aggregateCounts.increased) }}</span>
        </div>
        <div class="aggregate-item">
          <div class="aggregate-label-row">
            <span class="aggregate-label">Decreased</span>
            <tooltip message="Gave less in the current period than the previous period." variant="question" size="11px" />
          </div>
          <span class="aggregate-count">{{ aggregateCounts.decreased }}</span>
          <span class="aggregate-percent">{{ aggregatePercent(aggregateCounts.decreased) }}</span>
        </div>
        <div class="aggregate-item">
          <div class="aggregate-label-row">
            <span class="aggregate-label">Lapsed</span>
            <tooltip message="Gave in the previous period but gave nothing in the current period." variant="question" size="11px" />
          </div>
          <span class="aggregate-count">{{ aggregateCounts.lapsed }}</span>
          <span class="aggregate-percent">{{ aggregatePercent(aggregateCounts.lapsed) }}</span>
        </div>
        <div class="aggregate-item">
          <div class="aggregate-label-row">
            <span class="aggregate-label">Reactivated</span>
            <tooltip message="Gave in the current period after skipping the previous period, but had given before." variant="question" size="11px" :position="{ bottom: '100%', right: '0', transform: 'translateX(-70%)', maxWidth: '200px' }" />
          </div>
          <span class="aggregate-count">{{ aggregateCounts.reactivated }}</span>
          <span class="aggregate-percent">{{ aggregatePercent(aggregateCounts.reactivated) }}</span>
        </div>
        <div class="aggregate-item">
          <div class="aggregate-label-row">
            <span class="aggregate-label">Inactive</span>
            <tooltip message="No giving in either period." variant="question" size="11px" :position="{transform: 'translateX(-80%)', maxWidth: '150px'}" />
          </div>
          <span class="aggregate-count">{{ aggregateCounts.inactive }}</span>
          <span class="aggregate-percent">{{ aggregatePercent(aggregateCounts.inactive) }}</span>
        </div>
      </div>
    </div>

    <!-- Donor Giving Over Time -->
    <div v-if="reportLoaded" class="report-section chart-section">
      <div class="section-title">Donor Giving Over Time</div>

      <div class="chart-layout">
        <!-- Donor Selector (left) -->
        <div class="chart-sidebar">
          <div class="controls-container">
            <div class="w-full flex flex-row justify-between mb-1">
              <div class="section-label">Compare Donors</div>
              <div class="font-semibold text-gray-500 text-xs">
                ({{ selectedDonors.length }}/{{ maxDonors }})
              </div>
            </div>
            <div v-if="!canAddDonor" class="max-donors-message">
              Maximum of {{ maxDonors }} donors reached. Remove a donor to add another.
            </div>
            <div class="flex flex-row gap-2">
              <div class="search-input-wrapper flex-1">
                <icon icon="search" class="search-icon" />
                <input
                  v-model="donorSearchQuery"
                  type="text"
                  placeholder="Search by name or email..."
                  class="search-input"
                />
                <button v-if="donorSearchQuery" class="clear-search-btn" @click="donorSearchQuery = ''">
                  <icon icon="x" />
                </button>
              </div>
              <button
                :disabled="selectedDonors.length === 0"
                class="btn btn-outline btn-secondary btn-small"
                @click="clearSelectedDonors"
              >
                <span>Clear</span>
              </button>
            </div>
          </div>
          <!-- Trend Filter -->
          <div class="trend-filter flex flex-row items-center gap-2 pb-2">
            <label class="section-title !mb-0 whitespace-nowrap">Filter by:</label>
            <select
              v-model="selectedTrendFilter"
              class="trend-select flex-1"
            >
              <option :value="null">All Trends</option>
              <option value="new">New</option>
              <option value="increased">Increased</option>
              <option value="decreased">Decreased</option>
              <option value="lapsed">Lapsed</option>
              <option value="reactivated">Reactivated</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div class="donors-list clean-scrollbar">
            <div v-if="donorsLoading" class="flex justify-center items-center h-full">
              <loading-spinner size="small" />
            </div>
            <div v-else-if="filteredDonors.length === 0 && (donorSearchQuery || selectedTrendFilter)" class="empty-state">
              <p class="empty-primary">No donors found</p>
              <p class="empty-secondary">Try adjusting your search criteria</p>
            </div>

            <div class="donor-list-container">
              <div
                v-for="donor in visibleDonors"
                :key="donor.id"
                class="donor-info-card"
                :class="[
                  donorIsSelected(donor) ? 'selected-donor' : 'unselected-donor',
                  !canAddDonor && !donorIsSelected(donor) ? 'disabled-donor' : ''
                ]"
                @click="toggleChartDonor(donor)"
              >
                <div class="user-info">
                  <div class="user-details">
                    <div class="user-name">
                      <span>{{ donor.user_details?.f_name }}</span>
                      <span> {{ donor.user_details?.l_name }}</span>
                      <span v-if="chartUpdating && donorIsSelected(donor)" class="loading-spinner-small"></span>
                    </div>
                    <div class="user-email">{{ donor.user_details?.email }}</div>
                  </div>
                </div>
                <div class="donor-meta">
                  <span v-if="getDonorTrend(donor)" class="trend-badge" :style="{ color: trendColor(getDonorTrend(donor)) }">
                    {{ getDonorTrend(donor) }}
                  </span>
                </div>
                <div class="user-actions">
                  <button v-if="!donorIsSelected(donor)" class="add-btn" title="Add to chart">
                    <icon icon="plus" size="16px" />
                  </button>
                  <button v-else class="remove-btn" title="Remove from chart">
                    <icon icon="check" color="darkgreen" class="default-icon" />
                    <icon icon="x" color="red" class="hover-icon" />
                  </button>
                </div>
              </div>
              <button
                v-if="hasMoreDonors"
                class="load-more-donors-btn"
                @click="paginatedDonorList += 50"
              >
                Show More ({{ filteredDonors.length - paginatedDonorList }} remaining)
              </button>
            </div>
          </div>
        </div>

        <!-- Line Chart (right) -->
        <div class="chart-main">
          <div v-if="chartLoading" class="chart-placeholder">
            <loading-spinner size="small" />
          </div>
          <div v-else-if="chartData" class="chart-container">
            <BaseLineChart
              :data="chartData"
              :options="chartOptions"
              y-prefix="$"
            />
          </div>
          <div v-else class="chart-placeholder">
            Select donors to compare their giving over time.
          </div>
        </div>
      </div>
    </div>
  </div>
  <popup ref="popup" :message="popupMessage" :duration="3000" />
</template>

<style lang="scss" scoped>
.giving-trend-report {
  @apply w-full flex flex-col h-full max-h-full overflow-y-auto;
  @apply p-4;
}

.header {
  @apply pb-4 border-b-2 mb-4 border-gray-200;
}

.report-section {
  @apply border border-gray-200 rounded-lg p-4 mb-4 bg-white;
}

.section-title {
  @apply text-sm font-semibold text-gray-700 mb-3;
}

// Period Pills
.period-pills {
  @apply flex flex-row gap-0 mb-4 border border-gray-300 rounded-lg overflow-hidden w-fit;
}

.period-pill {
  @apply px-4 py-2 text-sm font-medium text-gray-600 bg-white border-r border-gray-300 transition-colors;

  &:last-child {
    @apply border-r-0;
  }

  &:hover {
    @apply bg-gray-50;
  }

  &.active {
    @apply bg-default_secondary text-white;
  }
}

// Period Dates
.period-dates {
  @apply flex flex-row gap-8 mb-4;
}

.period-date-group {
  @apply flex flex-col gap-1;
}

.period-date-label {
  @apply text-xs font-semibold text-gray-500 uppercase;
}

.period-date-display {
  @apply text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 bg-gray-50;
}

.date-input {
  @apply py-2 px-3 border border-gray-300 rounded-md text-sm outline-none;
  @apply transition-all duration-150 focus:border-blue-500 focus:ring-2 focus:ring-blue-100;
}

// Action Buttons
.action-buttons {
  @apply flex flex-row gap-3;
}

// Aggregate Section
.aggregate-section {
  @apply bg-gray-50;
}

.aggregate-header {
  @apply flex flex-row justify-between items-center mb-3;
}

.aggregate-total {
  @apply text-sm text-gray-500 font-medium;
}

.aggregate-row {
  @apply flex flex-row justify-between items-center;
}

.aggregate-item {
  @apply flex flex-col items-center gap-1 px-4;
}

.aggregate-label-row {
  @apply flex flex-row items-center gap-1;
}

.aggregate-label {
  @apply text-sm text-gray-600 font-semibold;
}

.aggregate-count {
  @apply text-2xl font-bold text-gray-800;
}

.aggregate-percent {
  @apply text-xs text-gray-400;
}

// Donor Selector
.donor-selector {
  @apply mb-4;
}

.controls-container {
  @apply mb-2;

  .search-input-wrapper {
    @apply relative flex items-center w-full;
  }

  .search-icon {
    @apply absolute left-3 text-gray-400 pointer-events-none;
  }

  .search-input {
    @apply w-full py-2 px-3 pl-9 pr-10 border border-gray-300 rounded-md text-sm outline-none;
    @apply transition-all duration-150 focus:border-blue-500 focus:ring-2 focus:ring-blue-100;
  }
}

.max-donors-message {
  @apply text-xs text-orange-500 font-medium mb-2;
}

.section-label {
  @apply font-semibold;
}

.donors-list {
  @apply h-64 flex flex-col gap-1 overflow-y-auto border rounded-md bg-white;
  @apply p-3;
}

.empty-state {
  @apply text-center py-12;
}

.empty-primary {
  @apply text-gray-900 font-medium mb-1;
}

.empty-secondary {
  @apply text-gray-500 text-sm;
}

.donor-list-container {
  @apply flex flex-col gap-2;

  .donor-info-card {
    @apply flex items-center justify-between p-2 bg-gray-50;
    @apply border border-gray-200 rounded-lg cursor-pointer transition-all;
    @apply h-16;

    &:hover {
      @apply shadow-sm border-gray-300;
    }

    &.disabled-donor {
      @apply opacity-50 cursor-not-allowed;
    }
  }

  .selected-donor {
    @apply bg-green-50 border-green-800;

    &:hover {
      @apply bg-green-100 border-green-800;
    }
  }

  .user-info {
    @apply flex items-center gap-3 flex-1 min-w-0;
  }

  .user-details {
    @apply min-w-0 flex-1;
  }

  .user-name {
    @apply font-medium text-gray-900 flex gap-1;
  }

  .user-email {
    @apply text-sm text-gray-500 truncate;
  }
}

.donor-meta {
  @apply px-3;
}

.trend-badge {
  @apply text-xs font-semibold capitalize;
}

.loading-spinner-small {
  @apply inline-block w-3 h-3 ml-1 border-2 border-blue-500 border-t-transparent rounded-full;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.remove-btn .hover-icon { display: none; }
.remove-btn .default-icon { display: inline; }
.remove-btn:hover .hover-icon { display: inline; }
.remove-btn:hover .default-icon { display: none; }

// Chart
.chart-layout {
  @apply flex flex-row gap-4 items-stretch;
}

.chart-sidebar {
  @apply w-80 flex-shrink-0 flex flex-col;
}

.chart-main {
  @apply flex-1 min-w-0;
}

.chart-container {
  @apply h-full;
}

.chart-placeholder {
  @apply flex items-center justify-center h-full text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg;
}

.load-more-donors-btn {
  @apply w-full py-2 mt-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors;
}

.icon-hover-white:hover {
  --icon-color: white;
}
.trend-filter {
  @apply mt-2;
}

.trend-select {
  @apply w-full h-8 py-1.5 px-2 border border-gray-300 rounded-md text-xs outline-none bg-white;
  @apply transition-all duration-150 focus:border-black focus:ring-0;
}
</style>
